import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import TRABrandLockup from "../../components/brand/TRABrandLockup";
import { useActiveSport } from "../../sports/ActiveSportContext";
import { useAuth } from "../../hooks/useAuth";
import { useMyPlayerProfile } from "../profile/hooks/useMyPlayerProfile";
import { usePlayerMatchHistory } from "../profile/hooks/usePlayerMatchHistory";
import { buildSportSelectionCards } from "../../domain/sport/buildSportSelectionCards";
import { useProfilePlatformFonts } from "../profile/hooks/useProfilePlatformFonts";
import { usePlayerProfileTheme } from "../profile/hooks/usePlayerProfileTheme";
import { WELCOME_TAGLINE } from "../../config/brand";
import { isArenaEntryLocation } from "../../domain/sport/arenaEntryLocation";
import {
  acknowledgeSportSelectionThisSession,
  hasAcknowledgedSportSelectionThisSession,
} from "../../domain/sport/sportSelectionFrequency";
import { hasChosenActiveSport } from "../../domain/sport/activeSport";
import SportGlyph from "./SportGlyph";
import s from "./sportSelection.module.css";
import { sportSelectionAccentStyle } from "./sportSelectionAccents";

/**
 * Full-screen Sport Mode overlay — no route, no history entry.
 * Cold-start + Me → Change Sport share this surface.
 * Confirming a sport lands on Home.
 * Suppressed during frictionless Arena invitation entry.
 */
export default function SportModeOverlayHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const {
    selectionOpen,
    selectionMode,
    entrySplash,
    sportId,
    playableSports,
    confirmSport,
    closeSportSelection,
    hasChosen,
  } = useActiveSport();

  const arenaEntry = isArenaEntryLocation(location);
  // Join flow may acknowledge sport without remounting the provider open-state.
  const suppressStaleLaunch =
    selectionMode !== "change" &&
    hasAcknowledgedSportSelectionThisSession() &&
    hasChosenActiveSport();

  useEffect(() => {
    if (!arenaEntry || selectionMode === "change") return;
    acknowledgeSportSelectionThisSession();
    closeSportSelection();
  }, [arenaEntry, selectionMode, closeSportSelection]);

  if (loading || !isAuthenticated) return null;
  if ((arenaEntry && selectionMode !== "change") || suppressStaleLaunch) return null;
  if (!selectionOpen && !entrySplash) return null;

  const onConfirm = (id) => {
    confirmSport(id);
    if (isArenaEntryLocation(location)) return;
    navigate("/", { replace: true });
  };

  return createPortal(
    <SportSelectionOverlay
      mode={selectionMode}
      activeSportId={sportId}
      playableSports={playableSports}
      hasChosen={hasChosen}
      entrySplash={entrySplash}
      onConfirm={onConfirm}
      onCancel={selectionMode === "change" ? closeSportSelection : undefined}
    />,
    document.body
  );
}

const MOSAIC_TILE_COUNT = 10;

/** Organic scatter — positions stable; rotation applied randomly per overlay open. */
const MOSAIC_SCATTER = Object.freeze([
  { left: 6, top: 18, scale: 0.88 },
  { left: 28, top: 4, scale: 1.02 },
  { left: 52, top: 22, scale: 0.94 },
  { left: 78, top: 8, scale: 1.06 },
  { left: 92, top: 38, scale: 0.9 },
  { left: 14, top: 52, scale: 1 },
  { left: 38, top: 42, scale: 0.92 },
  { left: 62, top: 58, scale: 1.04 },
  { left: 84, top: 48, scale: 0.96 },
  { left: 48, top: 28, scale: 0.86 },
]);

function mosaicRotationDeg() {
  return Math.round(Math.random() * 64 - 32);
}

function buildMosaicTiles(cards) {
  if (!cards?.length) return [];
  const tiles = [];
  let i = 0;
  while (tiles.length < MOSAIC_TILE_COUNT) {
    const card = cards[i % cards.length];
    const layout = MOSAIC_SCATTER[tiles.length % MOSAIC_SCATTER.length];
    tiles.push({
      ...card,
      layout: { ...layout, rotate: mosaicRotationDeg() },
    });
    i += 1;
  }
  return tiles;
}

function sportCardDescription(card) {
  if (card.isContinue && card.contextLine) {
    return card.contextLine.startsWith("Played")
      ? card.contextLine.replace(/^Played /, "Last played ")
      : card.contextLine;
  }
  return card.tagline || card.contextLine;
}

function SportSelectionOverlay({
  mode,
  activeSportId,
  playableSports,
  hasChosen,
  entrySplash,
  onConfirm,
  onCancel,
}) {
  useProfilePlatformFonts();
  const shellRef = useRef(null);
  usePlayerProfileTheme(shellRef, true);
  const panelRef = useRef(null);
  const taglineParts = WELCOME_TAGLINE.split(". ");
  const taglineLineOne = taglineParts[0] ? `${taglineParts[0]}.` : WELCOME_TAGLINE;
  const taglineLineTwo = taglineParts[1] || "";
  const { view } = useMyPlayerProfile({ enabled: true });
  const playerId = view?.playerId;
  const { items: matchCards } = usePlayerMatchHistory(playerId, 80, null);

  const cards = useMemo(
    () =>
      buildSportSelectionCards({
        activeSportId: hasChosen ? activeSportId : null,
        matchCards,
        modules: playableSports,
      }),
    [activeSportId, hasChosen, matchCards, playableSports]
  );

  const mosaicTiles = useMemo(() => buildMosaicTiles(cards), [cards]);

  const [pendingId, setPendingId] = useState(null);
  const [exiting, setExiting] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const finish = (id) => {
    if (pendingId) return;
    setPendingId(id);
    setExiting(true);
    onConfirm(id);
  };

  if (entrySplash) {
    return (
      <div
        ref={shellRef}
        className={`tra-player-profile tra-visual-v2 ${s.overlay} ${s.overlaySplash}`}
        data-testid="sport-entry-splash"
        role="status"
      >
        <div className={s.splashInner}>
          <div className={s.splashGlyph}>
            <SportGlyph
              glyph={entrySplash.glyph}
              emoji={entrySplash.emoji}
              size={56}
            />
          </div>
          <p className={s.splashName}>{entrySplash.displayName}</p>
          <p className={s.splashPhrase}>{entrySplash.phrase}</p>
        </div>
      </div>
    );
  }

  const title =
    mode === "change" ? "Switch your active sport" : "Which sport are you playing today?";
  const subtitle =
    mode === "change"
      ? "Home, Profile, and rankings follow the sport you pick."
      : "Pick one to personalize scoring, stats, and your home feed.";

  return (
    <div
      ref={shellRef}
      className={`tra-player-profile tra-visual-v2 ${s.overlay} ${entered ? s.overlayEntered : ""} ${exiting ? s.overlayExit : ""}`}
      data-testid="sport-selection-overlay"
      data-mode={mode}
      data-tra-weight-policy="platform"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sport-mode-title"
    >
      <div className={s.backdrop} aria-hidden />
      <div className={s.frame}>
        <div className={s.inner} ref={panelRef}>
          <div className={s.mosaicWrap} aria-hidden>
            <div className={s.mosaicScatter}>
              {mosaicTiles.map((tile, index) => (
                <div
                  key={`${tile.sportId}-${index}`}
                  className={s.mosaicCell}
                  style={{
                    ...sportSelectionAccentStyle(),
                    "--mosaic-x": `${tile.layout.left}%`,
                    "--mosaic-y": `${tile.layout.top}%`,
                    "--mosaic-rot": `${tile.layout.rotate}deg`,
                    "--mosaic-scale": tile.layout.scale,
                  }}
                >
                  <span className={s.glyph}>
                    <SportGlyph glyph={tile.glyph} emoji={tile.emoji} size={24} />
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={s.brand}>
            <TRABrandLockup size="lg" className={s.brandLockup} />
            <p className={s.brandTagline}>
              {taglineLineOne}{" "}
              {taglineLineTwo ? (
                <span className={s.brandTaglineAccent}>{taglineLineTwo}</span>
              ) : null}
            </p>
          </div>

          <header className={s.header}>
            <h1 id="sport-mode-title" className={s.title} data-tra-text-bold="page-title">
              {title}
            </h1>
            <p className={s.subtitle}>{subtitle}</p>
          </header>

          <ul className={s.list} role="list">
            {cards.map((card) => (
              <li key={card.sportId}>
                <button
                  type="button"
                  className={`${s.card} ${card.isContinue ? s.cardContinue : ""} ${
                    pendingId === card.sportId ? s.cardPending : ""
                  }`}
                  style={sportSelectionAccentStyle()}
                  onClick={() => finish(card.sportId)}
                  disabled={Boolean(pendingId)}
                  data-testid={`sport-select-${card.sportId}`}
                >
                  <span className={s.emojiWrap} style={sportSelectionAccentStyle()}>
                    <SportGlyph
                      glyph={card.glyph}
                      emoji={card.emoji}
                      size={32}
                      className={s.glyph}
                    />
                  </span>
                  <span className={s.cardBody}>
                    <span className={s.cardName}>{card.displayName}</span>
                    <span className={s.cardMeta}>{sportCardDescription(card)}</span>
                  </span>
                  {card.isContinue ? (
                    <span className={s.continueBadge}>Continue</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>

          <p className={s.footnote}>
            You can change your active sport anytime from Profile. Match scoring still follows each
            arena&apos;s sport.
          </p>

          {onCancel ? (
            <button
              type="button"
              className={s.cancel}
              onClick={onCancel}
              disabled={Boolean(pendingId)}
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
